/**
 * StatCalculator - Single source of truth for all stat calculations
 *
 * Responsibilities:
 * - Calculate stat breakdowns with all bonuses (upgrades, tier, prestige, ascension, items)
 * - Cache calculations for performance
 * - Invalidate cache when equipment/upgrades change
 * - Emit events for UI updates
 * - Track time-of-day for conditional item effects
 */

export class StatCalculator {
    constructor(gameState) {
        this.gameState = gameState;
        this.currentTimeOfDay = 'day';

        // Cache for stat breakdowns: Map<cacheKey, breakdown>
        this.breakdownCache = new Map();

        // Cache for active item effects (recalculated when equipment changes)
        this.itemEffectsCache = null;
        this.itemEffectsCacheValid = false;

        // Event listeners
        this.listeners = new Set();

        // Stat types that exist
        // Core stats (upgradeable via car config)
        this.coreStatTypes = ['baseSpeed', 'acceleration', 'maxSpeed', 'cruiseDuration', 'cruiseCooldown', 'momentum', 'momentumRate', 'timeScale'];
        // Item-only stats (no upgrade tree, only from bespoke items)
        // Note: fuelTank and itemUpgradeBonus also have driver skill trees, but items can add to them
        this.itemOnlyStatTypes = ['mileMultiplier', 'salvageBonus', 'waveLuck', 'fuelTank', 'itemUpgradeBonus'];
        // All stat types combined
        this.statTypes = [...this.coreStatTypes, ...this.itemOnlyStatTypes];
    }

    /**
     * Subscribe to stat invalidation events
     * @param {Function} callback - Called with { types: string[] | null } when stats change
     * @returns {Function} Unsubscribe function
     */
    subscribe(callback) {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }

    /**
     * Emit stats-invalidated event to all listeners
     * @param {string[]|null} types - Specific stat types that changed, or null for all
     */
    _emit(types = null) {
        this.listeners.forEach(cb => {
            try {
                cb({ types });
            } catch (e) {
                console.error('[StatCalculator] Listener error:', e);
            }
        });
    }

    /**
     * Set current time of day (affects conditional item effects)
     * @param {string} timeOfDay - 'dawn', 'day', 'sunset', or 'night'
     */
    setTimeOfDay(timeOfDay) {
        if (this.currentTimeOfDay !== timeOfDay) {
            this.currentTimeOfDay = timeOfDay;
            this.invalidateItemEffects();
        }
    }

    /**
     * Invalidate item effects cache (call when equipment changes)
     */
    invalidateItemEffects() {
        this.itemEffectsCacheValid = false;
        this.breakdownCache.clear();
        this._emit(null); // All stats potentially affected
    }

    /**
     * Invalidate specific stat type (call when upgrade purchased)
     * @param {string} type - Stat type that changed
     */
    invalidateStat(type) {
        // Clear all cache entries for this stat type
        for (const key of this.breakdownCache.keys()) {
            if (key.startsWith(type + '-')) {
                this.breakdownCache.delete(key);
            }
        }
        this._emit([type]);
    }

    /**
     * Invalidate all cached data (call on car change, ascension, prestige)
     */
    invalidateAll() {
        this.breakdownCache.clear();
        this.itemEffectsCacheValid = false;
        this._emit(null);
    }

    /**
     * Calculate active item effects from equipped items
     * @returns {Object} Effects by stat type: { statType: { add, multiply } }
     */
    calculateActiveItemEffects() {
        if (this.itemEffectsCacheValid && this.itemEffectsCache) {
            return this.itemEffectsCache;
        }

        const equipped = this.gameState.getEquippedItems();

        // Initialize effect accumulators
        const effects = {};
        for (const type of this.statTypes) {
            effects[type] = { add: 0, multiply: 1 };
        }

        // Process each equipped item
        Object.values(equipped).forEach(item => {
            if (!item || !item.effects) return;

            // Note: Prestige Fusion Bonus is applied DURING fusion, not here
            // The bonus stats are regular additive values (not percentages)

            item.effects.forEach(effect => {
                // Check if condition is met
                if (!this._checkCondition(effect.condition)) {
                    return;
                }

                // Initialize effect type if needed
                if (!effects[effect.type]) {
                    effects[effect.type] = { add: 0, multiply: 1 };
                }

                // Determine if multiplicative
                const isMultiplier = effect.operation === 'multiply' ||
                    (effect.operation !== 'add' && (effect.type === 'acceleration' || effect.type === 'timeScale'));

                // Apply effect directly (no prestige multiplier - that's applied during fusion)
                if (isMultiplier) {
                    effects[effect.type].multiply *= effect.value;
                } else {
                    effects[effect.type].add += effect.value;
                }
            });

            // Apply all prestige bonus stats (additive regular stat values)
            const bonusStats = this.gameState.getItemPrestigeBonusStats(item);
            for (const bonus of bonusStats) {
                if (!effects[bonus.type]) {
                    effects[bonus.type] = { add: 0, multiply: 1 };
                }
                // Bonus stats are additive (regular stat values like +2.5 Max Speed)
                effects[bonus.type].add += bonus.value;
            }
        });

        this.itemEffectsCache = effects;
        this.itemEffectsCacheValid = true;
        return effects;
    }

    /**
     * Check if an effect's condition is met
     * @param {Object|undefined} condition - Effect condition
     * @returns {boolean} True if condition is met or no condition exists
     */
    _checkCondition(condition) {
        if (!condition) return true;
        if (condition.timeOfDay) {
            return condition.timeOfDay === this.currentTimeOfDay;
        }
        return true;
    }

    /**
     * Get stat breakdown with all bonuses
     * @param {string} type - Stat type
     * @param {number} level - Current upgrade level
     * @param {Object} carConfig - Car configuration
     * @returns {Object} Full breakdown with all components
     */
    getBreakdown(type, level, carConfig) {
        const cacheKey = `${type}-${level}-${this.gameState.state.currentCarId}`;

        if (this.breakdownCache.has(cacheKey)) {
            return this.breakdownCache.get(cacheKey);
        }

        const breakdown = this._calculateBreakdown(type, level, carConfig);
        this.breakdownCache.set(cacheKey, breakdown);
        return breakdown;
    }

    /**
     * Internal breakdown calculation
     */
    _calculateBreakdown(type, level, carConfig) {
        const ab = this.gameState.getAscensionBonus(type);
        const tierMultiplier = this.gameState.getTierStatMultiplier();
        const prestigeBonus = this.gameState.state.prestige?.skillBonuses?.[type] || 0;
        const prestigeMultiplier = 1 + prestigeBonus;
        const itemEffects = this.calculateActiveItemEffects()[type] || { add: 0, multiply: 1 };
        const maxLevel = this.gameState.getTierUpgradeCap();

        // Get collection milestone bonuses
        const collectionManager = this.gameState.getCollectionStateManager?.();
        const collectionRewards = collectionManager?.getMilestoneRewards?.() || {};
        const collectionBonus = collectionRewards[type] || 0;

        let baseValue, upgradeBonus, unit, nextLevelBonus;

        if (type === 'baseSpeed') {
            const increment = (carConfig.maxBaseSpeed - carConfig.baseSpeed) / 10;
            baseValue = carConfig.baseSpeed;
            upgradeBonus = level * increment;
            nextLevelBonus = increment;
            unit = 'MPH';
        } else if (type === 'acceleration') {
            const increment = (carConfig.maxAcceleration - carConfig.baseAcceleration) / 10;
            baseValue = carConfig.baseAcceleration;
            upgradeBonus = level * increment;
            nextLevelBonus = increment;
            unit = 'MPH/click';
        } else if (type === 'maxSpeed') {
            const increment = carConfig.maxSpeed / 10;
            baseValue = carConfig.maxSpeed;
            upgradeBonus = level * increment;
            nextLevelBonus = increment;
            unit = 'MPH';
        } else if (type === 'cruiseDuration') {
            const increment = (carConfig.maxCruiseDuration - carConfig.baseCruiseDuration) / 10;
            baseValue = carConfig.baseCruiseDuration;
            upgradeBonus = level * increment;
            nextLevelBonus = increment;
            unit = 's';
        } else if (type === 'cruiseCooldown') {
            const decrement = (carConfig.baseCruiseCooldown - 10) / 10;
            baseValue = carConfig.baseCruiseCooldown;
            upgradeBonus = -(level * decrement);
            nextLevelBonus = -decrement;
            unit = 's';
        } else if (type === 'momentum') {
            const increment = (carConfig.maxMomentumCap - carConfig.baseMomentumCap) / 10;
            baseValue = carConfig.baseMomentumCap;
            upgradeBonus = level * increment;
            nextLevelBonus = increment;
            unit = 'MPH';
        } else if (type === 'momentumRate') {
            const baseRate = carConfig.baseMomentumRate || 0.05;
            const maxRate = carConfig.maxMomentumRate || 0.10;
            const increment = (maxRate - baseRate) / 10;
            baseValue = baseRate;
            upgradeBonus = level * increment;
            nextLevelBonus = increment;
            unit = 'MPH/s';
        } else if (type === 'timeScale') {
            // timeScale doesn't have upgrades, but items can affect it
            baseValue = 1;
            upgradeBonus = 0;
            nextLevelBonus = 0;
            unit = 'x';
        }

        // Calculate tier bonus (applied to base + upgrades)
        // Tier multiplier applies to speed-based stats (MPH or MPH/s units)
        // It does NOT apply to time-based stats (cruiseDuration, cruiseCooldown)
        const beforeTier = baseValue + upgradeBonus;
        const isTimeBased = type === 'cruiseDuration' || type === 'cruiseCooldown';
        const tierBonus = isTimeBased ? 0 : beforeTier * (tierMultiplier - 1);

        // Calculate after tier (tier is 0 for time-based stats)
        const afterTier = beforeTier + tierBonus;

        // GameState order for ALL stats:
        // base+upgrades → apply tier → add AB → apply prestige
        // Add Ascension Bonus BEFORE prestige (matches GameState calculation order)
        // Note: cruiseCooldown does NOT receive AB to keep values predictable through upgrade chain
        const afterTierWithAB = type === 'cruiseCooldown' ? afterTier : afterTier + ab;

        let prestigeBonusValue, subtotal;
        if (type === 'cruiseCooldown') {
            // Cooldown: multiply by (1 - prestigeBonus) to REDUCE cooldown
            const prestigeReduction = 1 - prestigeBonus;
            prestigeBonusValue = afterTierWithAB - (afterTierWithAB * prestigeReduction);
            subtotal = afterTierWithAB * prestigeReduction;
        } else {
            // All other stats: multiply by (1 + prestigeBonus) to INCREASE
            prestigeBonusValue = afterTierWithAB * prestigeBonus;
            subtotal = afterTierWithAB * prestigeMultiplier;
        }

        // Calculate item bonuses (add then multiply)
        const itemAddBonus = itemEffects.add;
        const afterItemAdd = subtotal + itemAddBonus;
        const itemMultiplyBonus = afterItemAdd * (itemEffects.multiply - 1);
        const afterItems = afterItemAdd * itemEffects.multiply;

        // Add collection milestone bonus (flat additive, applied last)
        let total = afterItems + collectionBonus;

        // Clamp cruiseCooldown to minimum 5 seconds (matches GameState.js)
        if (type === 'cruiseCooldown') {
            total = Math.max(5, total);
        }

        return {
            baseValue,
            upgradeBonus,
            tierBonus,
            tierMultiplier,
            prestigeBonusValue,
            prestigeBonus,
            ab,
            itemAddBonus,
            itemMultiplyBonus,
            itemMultiplier: itemEffects.multiply,
            collectionBonus,
            total,
            unit,
            nextLevelBonus: level < maxLevel ? nextLevelBonus : null,
            level,
            maxLevel
        };
    }

    /**
     * Get formatted current stat value
     * @param {string} type - Stat type
     * @param {number} level - Current upgrade level
     * @param {Object} carConfig - Car configuration
     * @returns {string} Formatted stat value
     */
    getFormattedValue(type, level, carConfig) {
        const breakdown = this.getBreakdown(type, level, carConfig);
        const value = breakdown.total;

        // Time-based stats show with 1 decimal for star differentiation
        if (type === 'cruiseDuration' || type === 'cruiseCooldown') {
            const formatted = value.toFixed(1);
            // Show (MIN) indicator when cooldown is at the 5s floor
            if (type === 'cruiseCooldown' && value <= 5) {
                return `${formatted}${breakdown.unit} (MIN)`;
            }
            return `${formatted}${breakdown.unit}`;
        } else if (type === 'momentum') {
            // Momentum shows with + prefix and one decimal
            return `+${value.toFixed(1)} ${breakdown.unit}`;
        } else if (type === 'momentumRate') {
            // Momentum rate shows with + prefix and three decimals (small values)
            return `+${value.toFixed(3)} ${breakdown.unit}`;
        } else if (type === 'timeScale') {
            return `${value.toFixed(2)}${breakdown.unit}`;
        } else {
            // All other stats show one decimal place for consistency
            return `${value.toFixed(1)} ${breakdown.unit}`;
        }
    }

    /**
     * Check if stat has any bonuses (for showing info icon)
     * @param {string} type - Stat type
     * @returns {boolean} True if stat has any bonuses
     */
    hasModifiers(type) {
        const ab = this.gameState.getAscensionBonus(type);
        const prestigeBonus = this.gameState.state.prestige?.skillBonuses?.[type] || 0;
        const tierMultiplier = this.gameState.getTierStatMultiplier();
        const itemEffects = this.calculateActiveItemEffects()[type] || { add: 0, multiply: 1 };
        const collectionManager = this.gameState.getCollectionStateManager?.();
        const collectionRewards = collectionManager?.getMilestoneRewards?.() || {};
        const collectionBonus = collectionRewards[type] || 0;

        return ab !== 0 ||
               prestigeBonus > 0 ||
               tierMultiplier > 1 ||
               itemEffects.add !== 0 ||
               itemEffects.multiply !== 1 ||
               collectionBonus !== 0;
    }

    /**
     * Apply item effects to a base stat value (for game logic)
     * @param {string} statType - Stat type
     * @param {number} baseValue - Base value before items
     * @returns {number} Modified value with item effects
     */
    applyItemEffects(statType, baseValue) {
        const effects = this.calculateActiveItemEffects()[statType];
        if (!effects) return baseValue;
        return (baseValue + effects.add) * effects.multiply;
    }

    /**
     * Apply all bonuses (item effects + collection bonuses) to a base stat value
     * This is the complete formula: (baseValue + itemAdd) * itemMultiply + collectionBonus
     * @param {string} statType - Stat type
     * @param {number} baseValue - Base value before bonuses (already includes upgrades, tier, prestige)
     * @returns {number} Modified value with all bonuses applied
     */
    applyAllBonuses(statType, baseValue) {
        // Apply item effects first
        const effects = this.calculateActiveItemEffects()[statType] || { add: 0, multiply: 1 };
        let value = (baseValue + effects.add) * effects.multiply;

        // Then add collection milestone bonus (flat additive, applied last)
        const collectionManager = this.gameState.getCollectionStateManager?.();
        const collectionRewards = collectionManager?.getMilestoneRewards?.() || {};
        const collectionBonus = collectionRewards[statType] || 0;
        value += collectionBonus;

        return value;
    }

    /**
     * Get collection milestone bonus for a stat type
     * @param {string} statType - Stat type
     * @returns {number} Collection bonus value (0 if none)
     */
    getCollectionBonus(statType) {
        const collectionManager = this.gameState.getCollectionStateManager?.();
        const collectionRewards = collectionManager?.getMilestoneRewards?.() || {};
        return collectionRewards[statType] || 0;
    }

    /**
     * Get debug string of active item effects
     * @returns {string} Human-readable effects summary
     */
    getActiveEffectsDebug() {
        const effects = this.calculateActiveItemEffects();
        const debug = [];

        Object.keys(effects).forEach(stat => {
            const effect = effects[stat];
            if (effect.add !== 0 || effect.multiply !== 1) {
                debug.push(`${stat}: +${effect.add.toFixed(1)}, x${effect.multiply.toFixed(2)}`);
            }
        });

        return debug.length > 0 ? debug.join(' | ') : 'No active effects';
    }

    /**
     * Get item-only stat value (for stats that only come from items, no upgrade tree)
     * @param {string} type - Stat type (mileMultiplier, salvageBonus, waveLuck, fuelTank, itemUpgradeBonus)
     * @returns {Object} { total, add, multiply, formatted }
     */
    getItemOnlyStat(type) {
        if (!this.itemOnlyStatTypes.includes(type)) {
            console.warn(`[StatCalculator] ${type} is not an item-only stat`);
            return { total: 0, add: 0, multiply: 1, formatted: '0' };
        }

        const effects = this.calculateActiveItemEffects()[type] || { add: 0, multiply: 1 };

        // Item-only stats start at 0 (no base) and accumulate from items
        // add values are additive, multiply is product of all multipliers
        const total = effects.add * effects.multiply;

        // Format based on stat type
        let formatted;
        if (type === 'mileMultiplier') {
            // Display as multiplier (e.g., "1.30x" - base 1.0 + bonus)
            const multiplier = 1 + total;
            formatted = `${multiplier.toFixed(2)}x`;
        } else if (type === 'salvageBonus') {
            // Display as percentage boost (e.g., "+30%")
            formatted = total > 0 ? `+${(total * 100).toFixed(0)}%` : '0%';
        } else if (type === 'waveLuck') {
            // Display as multiplier (e.g., "1.30x")
            const multiplier = 1 + total;
            formatted = `${multiplier.toFixed(2)}x`;
        } else if (type === 'fuelTank') {
            // Display as duration (e.g., "+30m" or "+1h 30m")
            if (total <= 0) {
                formatted = '0';
            } else if (total < 60) {
                formatted = `+${Math.round(total)}s`;
            } else if (total < 3600) {
                const mins = Math.floor(total / 60);
                const secs = Math.round(total % 60);
                formatted = secs > 0 ? `+${mins}m ${secs}s` : `+${mins}m`;
            } else {
                const hours = Math.floor(total / 3600);
                const mins = Math.round((total % 3600) / 60);
                formatted = mins > 0 ? `+${hours}h ${mins}m` : `+${hours}h`;
            }
        } else if (type === 'itemUpgradeBonus') {
            // Display as multiplier (e.g., "1.10x")
            const multiplier = 1 + total;
            formatted = `${multiplier.toFixed(2)}x`;
        } else {
            formatted = total.toFixed(2);
        }

        return {
            total,
            add: effects.add,
            multiply: effects.multiply,
            formatted
        };
    }

    /**
     * Check if any item-only stats have active effects
     * @returns {Object} Map of stat type to { active: boolean, value: number }
     */
    getActiveItemOnlyStats() {
        const result = {};
        for (const type of this.itemOnlyStatTypes) {
            const stat = this.getItemOnlyStat(type);
            result[type] = {
                active: stat.total > 0,
                value: stat.total,
                formatted: stat.formatted
            };
        }
        return result;
    }
}
